// ---------------------------------------------------------------------------
// Breadcrumb Renderer (editor key: Breadcrumb)
// ---------------------------------------------------------------------------
// Navigation primitive — publishes the editor `Breadcrumb` block as a
// semantic `<nav>` containing an ordered list of crumbs parsed from a
// pipe-delimited textarea (`Label | URL` per line). The last crumb is the
// current page and is rendered without an anchor (per WAI-ARIA practice).
// All links route through `url()` so `javascript:` schemes resolve to `#`,
// and the visible separator is escaped via `text()`.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface BreadcrumbProps {
  items?: string;
  separator?: string;
}

interface Crumb {
  label: string;
  href: string;
}

const MAX_CRUMBS = 12;

function parseItems(value: unknown): Crumb[] {
  if (typeof value !== "string") return [];
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [labelPart, urlPart] = line.split("|");
      return {
        label: (labelPart ?? "").trim(),
        href: (urlPart ?? "").trim(),
      };
    })
    .filter((crumb) => crumb.label.length > 0)
    .slice(0, MAX_CRUMBS);
}

function normalizeSeparator(value: unknown): string {
  if (typeof value !== "string") return "/";
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 4) return "/";
  return trimmed;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as BreadcrumbProps;

  const crumbs = parseItems(p.items);
  if (crumbs.length === 0) {
    return "<!-- breadcrumb missing items -->";
  }

  const separator = normalizeSeparator(p.separator);
  const lastIndex = crumbs.length - 1;

  const items = crumbs
    .map((crumb, index) => {
      const isLast = index === lastIndex;
      const separatorHtml = index > 0
        ? `<span class="ease-breadcrumb__separator" aria-hidden="true">${text(separator)}</span>`
        : "";
      const inner = isLast
        ? `<span class="ease-breadcrumb__current" aria-current="page">${text(crumb.label)}</span>`
        : `<a class="ease-breadcrumb__link" href="${url(crumb.href || "#")}">${text(crumb.label)}</a>`;
      return `    <li class="ease-breadcrumb__item">${separatorHtml}${inner}</li>`;
    })
    .join("\n");

  return `<nav class="ease-section ease-breadcrumb" aria-label="Breadcrumb">
  <ol class="ease-breadcrumb__list" role="list">
${items}
  </ol>
</nav>`;
}

registerRenderer("breadcrumb", { render });
